import styles from "../style/chat_body.module.css";
import { MessageFrom } from "../../../../domain/enum/message_from";
import moment from "moment";
import useChatItemHook from "../hooks/useChatItemHook";
import { useSelector, useDispatch } from "react-redux";
import { chatActions, galleryActions } from "../../../di/di";
import { Heart, Check, CheckCheck } from "lucide-react";
import { MessageStatus } from "../../../../domain/enum/message_status";
import React from "react";
import DateSeparator from "./chat_date_seperator";

const ChatItemComp = ({ item, index, onMessageClick }) => {
  const dispatch = useDispatch();
  const imgEve = "/images/eve_profile.svg";
  const images = item.images;
  const hasImage = images.length > 0;
  const user = useSelector((state) => state.user.user);
  const avatarProfile = useSelector((state) => state.modelProfile.profile);
  const { handleOnClick, timestampRef, showTimestamp, messageStatus } = useChatItemHook({ status: item.status, uniqueKey: item.uniqueKey });
  const handleMessageClick = () => onMessageClick && onMessageClick(item);
  const onImageClick = (imageItem) => {
    handleMessageClick();
    if (!user.tierId && imageItem.isBlur) {
      dispatch(chatActions.setShowSubscriptionModal(true));
    } else {
      dispatch(galleryActions.showPhotoViewer(imageItem));
    }
  };
  const timestamp = new Date(item.dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });

  if (item.from === MessageFrom.avatar) {
    return (
      <div className="max-w-[70%]">
        <div className="h-2" />
        <div className="flex flex-row items-start space-x-3 px-5">
          <div className="flex flex-col">
            <div className="relative w-8 h-8 mt-1 rounded-full bg-gradient-to-r from-red-800 via-red-900 to-red-950 flex items-center justify-center shadow-md shrink-0">
              <div
                className="absolute rounded-full w-8 h-8"
                style={{ background: `url('/images/eve_profile.webp') top / cover`, backgroundRepeat: "no-repeat" }}
              />
              <div className="absolute flex items-center justify-center">
                <div className="rounded-full size-8 overflow-hidden flex items-center justify-center ring-1 ring-red-700">
                  {(avatarProfile?.profileUrl ?? "") !== "" && (
                    <div
                      className="rounded-full size-8 overflow-hidden flex items-center justify-center"
                      style={{ background: `url('${avatarProfile?.profileUrl}') top / cover`, backgroundRepeat: "no-repeat" }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div
              className={`flex flex-col text-base text-white rounded-2xl rounded-tl-sm px-4 bg-white/10 backdrop-blur-sm border border-red-900/10 shadow-md`}
              style={{
                background: "linear-gradient(to bottom right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))",
              }}
              onClick={handleOnClick}
            >
              <div className="h-3 truncate" />
              {item.message}
              <div className="h-2" />
              {aiResponseTimestamp(timestamp, showTimestamp, timestampRef, true)}
              <div className="h-3" />
            </div>
            {hasImage && (
              <div className="flex flex-col">
                <div className="h-1" />
                <div
                  className="relative w-full sm:w-80 h-auto min-h-64 sm:min-h-80 md:min-h-96 rounded-xl cursor-pointer"
                  onClick={() => onImageClick(images[0])}
                >
                  <img src={images[0].imageUrl} alt="" className="rounded-xl inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-gray-950" />
                  {images[0].isBlur && (
                    <div className="absolute top-0 left-0 bottom-0 right-0 rounded-xl rounded-bl-sm flex flex-col items-center justify-center space-x-1 w-full bg-black bg-opacity-30 hover:bg-opacity-40 transition-opacity duration-300">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-900 via-red-950 to-red-900 flex items-center justify-center mb-3 shadow-lg shadow-red-900/40">
                        <Heart size={16} className="text-pink-200" />
                      </div>
                      <div className="bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full px-4 py-1 text-white font-medium text-xs shadow-lg shadow-red-900/30">
                        Exclusive
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="h-2" />
      </div>
    );
  } else if (item.from === MessageFrom.user) {
    return (
      <div>
        <div className="flex flex-col items-end px-5" onClick={handleMessageClick}>
          <div className="h-2" />
          <div className="flex flex-col  bg-rose-900  text-white rounded-2xl rounded-tr-sm px-4 backdrop-blur-sm border border-red-900/20 shadow-md text-base max-w-[60%]">
            <div className="h-2" />
            {item.message}
            <div className="h-2" />
            <div className="flex flex-row justify-end items-center space-x-1">
              <div ref={timestampRef} className="text-right text-xs text-pink-300/70 opacity-100">
                {timestamp}
              </div>
              {messageStatus === MessageStatus.sent && <Check size={16} className="text-pink-300/70" />}
              {messageStatus === MessageStatus.seen && <CheckCheck size={16} className="text-pink-200" />}
            </div>
            <div className="h-2" />
          </div>
          <div className="h-2" />
        </div>
      </div>
    );
  }
  return null;
};

const aiResponseTimestamp = (timestamp, showTimestamp, timestampRef, isViewedOnImage) => (
  <div ref={timestampRef} className="text-start text-xs text-white/40 opacity-100 h-3">
    {timestamp}
  </div>
);

export default ChatItemComp;

export const ChatMap = ({ messageList }) => {
  if (!messageList || messageList.length === 0) {
    return null;
  }
  const getDateKey = (isoString) => new Date(isoString).toISOString().split("T")[0];

  let lastDateKey = null;
  return (
    <div className="flex flex-col w-full">
      <div className="max-w-5xl w-full mx-auto">
        {messageList.map((item, index) => {
          const thisDateKey = getDateKey(item.dateString);

          const showSeparator = lastDateKey !== thisDateKey;
          lastDateKey = thisDateKey;

          return (
            <React.Fragment key={item.uniqueKey || index}>
              {showSeparator && <DateSeparator date={item.dateString} />}
              <div data-message-index={index}>
                <ChatItemComp item={item} index={index} />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
